import { useCallback } from 'react';
import hotToast from 'react-hot-toast';
import { useRealtimeSync } from './useRealtimeSync';

/**
 * 통합 알림 타입
 */
export type NotificationType =
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'NEW_SAMPLE_REQUEST'
  | 'SAMPLE_REQUEST_STATUS_CHANGE'
  | 'SAMPLE_REQUEST_UPDATED'
  | 'NEW_FEEDBACK'
  | 'FEEDBACK_STATUS_CHANGE'
  | 'NEW_FEEDBACK_REPLY'
  | 'PROJECT_UPDATE'
  | 'CONTRACT_REQUEST'
  | 'CONTRACT_SIGNED_BY_BRAND'
  | 'CONTRACT_COMPLETED'
  | 'QUOTATION_UPDATE'
  | 'NEW_QUOTATION';

/**
 * 알림 옵션
 */
interface NotifyOptions {
  /** 알림 메시지 */
  message: string;
  /** 알림 타입 */
  type?: NotificationType;
  /** 추가 데이터 (네비게이션 등에 사용) */
  data?: {
    requestId?: number;
    projectId?: number;
    feedbackId?: number;
    contractId?: number;
    quotationId?: number;
    [key: string]: any;
  };
  /** Toast만 표시 (bell에 추가하지 않음) - 기본: false */
  toastOnly?: boolean;
  /** Bell만 표시 (toast 표시하지 않음) - 기본: false */
  bellOnly?: boolean;
  /** Toast 지속 시간 (ms) - 기본: 4000 */
  duration?: number;
}

/**
 * 알림 타입에 따른 아이콘 반환
 */
const getNotificationIcon = (type: NotificationType): string => {
  switch (type) {
    case 'success':
      return '✅';
    case 'error':
      return '❌';
    case 'warning':
      return '⚠️';
    case 'info':
      return 'ℹ️';
    case 'NEW_SAMPLE_REQUEST':
      return '📋';
    case 'SAMPLE_REQUEST_STATUS_CHANGE':
      return '📝';
    case 'SAMPLE_REQUEST_UPDATED':
      return '✏️';
    case 'NEW_FEEDBACK':
      return '💬';
    case 'FEEDBACK_STATUS_CHANGE':
      return '💡';
    case 'NEW_FEEDBACK_REPLY':
      return '💌';
    case 'PROJECT_UPDATE':
      return '📁';
    case 'CONTRACT_REQUEST':
      return '📄';
    case 'CONTRACT_SIGNED_BY_BRAND':
      return '✍️';
    case 'CONTRACT_COMPLETED':
      return '✅';
    case 'QUOTATION_UPDATE':
    case 'NEW_QUOTATION':
      return '💰';
    default:
      return '🔔';
  }
};

// 전역 알림 리스트 (bell에서 접근)
let globalNotifications: Array<{
  type: string;
  message: string;
  timestamp: string | number;
  data?: any;
  read?: boolean;
}> = [];

let notificationListeners: Array<(notifications: typeof globalNotifications) => void> = [];

/**
 * 알림 리스트에 추가
 */
const addToNotificationList = (notification: {
  type: string;
  message: string;
  timestamp: string | number;
  data?: any;
}) => {
  globalNotifications = [notification, ...globalNotifications].slice(0, 50);
  notificationListeners.forEach(listener => listener(globalNotifications));
};

/**
 * 알림 리스너 등록
 */
export const subscribeToNotifications = (
  listener: (notifications: typeof globalNotifications) => void
) => {
  notificationListeners.push(listener);
  return () => {
    notificationListeners = notificationListeners.filter(l => l !== listener);
  };
};

/**
 * 현재 알림 목록 가져오기
 */
export const getNotifications = () => globalNotifications;

/**
 * 알림 클리어
 */
export const clearAllNotifications = () => {
  globalNotifications = [];
  notificationListeners.forEach(listener => listener(globalNotifications));
};

/**
 * 통합 알림 Hook
 *
 * Toast와 Bell 알림을 함께 관리합니다.
 *
 * @example
 * ```tsx
 * const { notify, notifySuccess, notifyError } = useNotification();
 *
 * // 기본 사용 (toast + bell)
 * notify({ message: 'Sample request submitted!', type: 'success' });
 *
 * // 간편 메서드
 * notifySuccess('Changes saved successfully!');
 * notifyError('Failed to save changes');
 *
 * // Toast만 표시
 * notify({ message: 'Loading...', type: 'info', toastOnly: true });
 *
 * // Bell에만 추가 (Toast 없이)
 * notify({ message: 'New update available', type: 'info', bellOnly: true });
 * ```
 */
export const useNotification = () => {
  // useRealtimeSync를 통해 WebSocket 알림도 통합
  const realtimeSync = useRealtimeSync();

  /**
   * 통합 알림 표시
   */
  const notify = useCallback((options: NotifyOptions) => {
    const {
      message,
      type = 'info',
      data,
      toastOnly = false,
      bellOnly = false,
      duration = 4000,
    } = options;

    const icon = getNotificationIcon(type);
    const timestamp = Date.now();

    // Toast 표시
    if (!bellOnly) {
      switch (type) {
        case 'success':
          hotToast.success(message, { duration });
          break;
        case 'error':
          hotToast.error(message, { duration });
          break;
        case 'warning':
          hotToast(message, { icon: '⚠️', duration });
          break;
        default:
          hotToast(message, { icon, duration });
          break;
      }
    }

    // Bell 알림 리스트에 추가
    if (!toastOnly) {
      addToNotificationList({
        type,
        message,
        timestamp,
        data,
      });
    }
  }, []);

  /**
   * 성공 알림 (간편 메서드)
   */
  const notifySuccess = useCallback((message: string, data?: NotifyOptions['data']) => {
    notify({ message, type: 'success', data });
  }, [notify]);

  /**
   * 에러 알림 (간편 메서드)
   */
  const notifyError = useCallback((message: string, data?: NotifyOptions['data']) => {
    notify({ message, type: 'error', data });
  }, [notify]);

  /**
   * 경고 알림 (간편 메서드)
   */
  const notifyWarning = useCallback((message: string, data?: NotifyOptions['data']) => {
    notify({ message, type: 'warning', data });
  }, [notify]);

  /**
   * 정보 알림 (간편 메서드)
   */
  const notifyInfo = useCallback((message: string, data?: NotifyOptions['data']) => {
    notify({ message, type: 'info', data });
  }, [notify]);

  /**
   * Toast만 표시 (간편 메서드)
   */
  const toastSuccess = useCallback((message: string) => {
    hotToast.success(message);
  }, []);

  const toastError = useCallback((message: string) => {
    hotToast.error(message);
  }, []);

  const toastInfo = useCallback((message: string) => {
    hotToast(message, { icon: 'ℹ️' });
  }, []);

  return {
    notify,
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,
    // Toast only 메서드 (기존 호환성)
    toastSuccess,
    toastError,
    toastInfo,
    // WebSocket 알림 관련 (useRealtimeSync에서 가져옴)
    connectionStatus: realtimeSync.connectionStatus,
    wsNotifications: realtimeSync.notifications,
    unreadCount: realtimeSync.unreadCount,
    markAsRead: realtimeSync.markAsRead,
    clearNotifications: realtimeSync.clearNotifications,
  };
};

export default useNotification;

/**
 * Toast methods object
 */
type ToastOptions = { id?: string; duration?: number; icon?: string };

const toastMethods = {
  success: (message: string, options?: ToastOptions) => {
    hotToast.success(message, options);
    addToNotificationList({
      type: 'success',
      message,
      timestamp: Date.now(),
    });
  },
  error: (message: string, options?: ToastOptions) => {
    hotToast.error(message, options);
    addToNotificationList({
      type: 'error',
      message,
      timestamp: Date.now(),
    });
  },
  info: (message: string, options?: ToastOptions) => {
    hotToast(message, { icon: 'ℹ️', ...options });
    addToNotificationList({
      type: 'info',
      message,
      timestamp: Date.now(),
    });
  },
  warning: (message: string, options?: ToastOptions) => {
    hotToast(message, { icon: '⚠️', ...options });
    addToNotificationList({
      type: 'warning',
      message,
      timestamp: Date.now(),
    });
  },
  // Loading toast (toast only, no bell)
  loading: (message: string, options?: { id?: string; duration?: number }) => hotToast.loading(message, options),
  // Dismiss toast
  dismiss: (toastId?: string) => hotToast.dismiss(toastId),
  // Custom toast
  custom: hotToast.custom,
  // Promise toast (toast only, no bell for intermediate states)
  promise: hotToast.promise,
};

/**
 * Drop-in replacement for react-hot-toast
 *
 * Shows toast AND adds to bell notification list.
 * Import this instead of 'react-hot-toast' to get both behaviors.
 *
 * @example
 * ```tsx
 * // Before (toast only):
 * import toast from 'react-hot-toast';
 *
 * // After (toast + bell):
 * import { toast } from '../hooks/useNotification';
 *
 * // Usage remains the same:
 * toast.success('Changes saved!');
 * toast.error('Failed to save');
 * toast('Hello world');
 * ```
 */
type ToastFunction = ((message: string, options?: { icon?: string; duration?: number }) => void) & typeof toastMethods;

export const toast: ToastFunction = Object.assign(
  (message: string, options?: { icon?: string; duration?: number }) => {
    hotToast(message, options);
    addToNotificationList({
      type: 'info',
      message,
      timestamp: Date.now(),
    });
  },
  toastMethods
);
